import React, { useState, useEffect, useContext, memo } from "react";
import "./AgendaItemCard.css";
import {
  LuVideo,
  LuMapPin,
  LuRadio,
  LuCalendar,
  LuClock,
  LuTimer,
  LuHourglass,
  LuCheck,
  LuGlobe,
  LuLock,
  LuArrowRight,
  LuShare2,
} from "react-icons/lu";
import { useCustomToast } from "../common/CustomToast";
import {
  getCategoryIcon,
  getCategoryLabel,
} from "../../constants/agendaConstants";
import {
  getFormattedDateRange,
  getAgendaItemDuration,
} from "../../utils/agendaUtils";
import { toggleFavoriteEvent } from "../../services/agendaApi";
import { savePostApi } from "../../services/feedApis";
import { Language } from "../../App";
import { Spinner } from "reactstrap";
import defaultAgenda1 from "../../assets/images/default_agenda_1.jpg";
import defaultAgenda2 from "../../assets/images/agenda_default2.png";

// Heart SVG Vector (outline by default, filled when favorited)
const HeartVectorIcon = ({ isLiked, size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={isLiked ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="agenda-action-svg"
  >
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

function getAgendaItemDetailsUrl(item) {
  if (!item) return window.location.href;
  const cleanId = item.id
    ? String(item.id).replace(/^(webinar-|session-|evt-)/, "")
    : "";

  let path = "/feed";
  if (
    item.type === "webinar" ||
    item.category === "webinar" ||
    item.entity_type === "webinar"
  ) {
    path = cleanId ? `/webinar-details/${cleanId}` : "/webinar-details";
  } else if (
    item.type === "clinical_session" ||
    item.category === "clinical_session" ||
    item.entity_type === "clinical_session"
  ) {
    path = cleanId ? `/session-details/${cleanId}` : "/session-details";
  } else if (
    item.type === "mentorship" ||
    item.category === "mentorship" ||
    item.entity_type === "mentorship" ||
    item.entity_type === "mentor_request"
  ) {
    path = cleanId ? `/mentorship-session-details/${cleanId}` : "/mentorship-session-details";
  } else {
    path = cleanId ? `/event-details/${cleanId}` : "/event-details";
  }

  return `${window.location.origin}${path}`;
}

function getCardCountdownText(countdown, lang) {
  if (!countdown) return lang?.upcoming || "Upcoming";
  const str = String(countdown).trim();
  if (/^(completed|ended)$/i.test(str)) {
    return lang?.completed || "Completed";
  }
  if (/^starts\s+in/i.test(str)) {
    return lang?.starts_in ? str.replace(/^starts\s+in\s+/i, `${lang.starts_in} `) : str;
  }
  if (/^starts\s+soon/i.test(str)) return lang?.starts_soon || str;
  if (/^in\s+/i.test(str)) {
    const timePart = str.replace(/^in\s+/i, "");
    return `${lang?.starts_in || "Starts in"} ${timePart}`;
  }
  if (/^tomorrow$/i.test(str)) return lang?.starts_tomorrow || "Starts Tomorrow";
  if (/^upcoming\s+in/i.test(str)) {
    return lang?.upcoming_in ? str.replace(/^upcoming\s+in\s+/i, `${lang.upcoming_in} `) : str;
  }
  return `${lang?.starts_in || "Starts in"} ${str}`;
}

function getEventDuration(item, lang) {
  return getAgendaItemDuration(item, lang);
}

function AgendaItemCard({ item, onSelect, onFavorite, onShare, viewMode }) {
  if (!item) return null;

  const { lang } = useContext(Language) || {};
  const { showToast } = useCustomToast();

  const resolveItemFavorited = (target) => {
    if (!target) return false;
    return Boolean(
      target.isFavorited ||
      target.is_favorite ||
      target.isFavorite ||
      target.favorite ||
      target.isSaved ||
      target.is_saved ||
      target.is_like ||
      target.isLike ||
      target.is_save ||
      target.isSave ||
      target.raw?.is_favorite ||
      target.raw?.favorite ||
      target.raw?.is_saved
    );
  };

  const [isFavorited, setIsFavorited] = useState(() => resolveItemFavorited(item));
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const isWebinar =
    item.type === "webinar" ||
    item.category === "webinar" ||
    item.entity_type === "webinar" ||
    String(item.id || "").toLowerCase().startsWith("webinar-");

  const isClinicalSession =
    item.type === "clinical_session" ||
    item.type === "clinical-session" ||
    item.type === "session" ||
    item.category === "clinical_session" ||
    item.category === "clinical-session" ||
    item.entity_type === "clinical_session" ||
    item.entity_type === "clinical-session" ||
    String(item.id || "").toLowerCase().startsWith("session-");

  const isMentorship =
    item.type === "mentorship" ||
    item.category === "mentorship" ||
    item.entity_type === "mentorship" ||
    item.entity_type === "mentor_request" ||
    item.entity_type === "mentor-request" ||
    String(item.id || "").toLowerCase().startsWith("mentorship-");

  const isEvent =
    !isWebinar &&
    !isClinicalSession &&
    !isMentorship &&
    (item.type === "event" ||
      item.category === "event" ||
      item.entity_type === "event" ||
      String(item.id || "").toLowerCase().startsWith("evt-") ||
      !item.type ||
      item.type === "all");

  useEffect(() => {
    setIsFavorited(resolveItemFavorited(item));
  }, [
    item.isFavorited,
    item.is_favorite,
    item.isFavorite,
    item.favorite,
    item.isSaved,
    item.is_saved,
    item.is_like,
    item.isLike,
    item.is_save,
    item.isSave,
    item.raw,
  ]);

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();

    // Clinical sessions and mentorship do not support favorite/save
    if (!isEvent && !isWebinar) {
      if (onFavorite) onFavorite(item);
      return;
    }

    const cleanId = String(
      item.apiId || item.originalId || item.raw_id || item.raw?.id || item.id || ""
    ).replace(/^(?:evt-|event-|webinar-)/i, "");

    if (!cleanId) {
      if (onFavorite) onFavorite(item);
      return;
    }

    const prevLiked = Boolean(isFavorited);
    const nextLiked = !prevLiked;
    // Instant state change (no spinner)
    setIsFavorited(nextLiked);

    if (isWebinar) {
      try {
        const response = await savePostApi({ post_id: cleanId });
        const determinedSaved =
          typeof response?.isSave === "boolean"
            ? response.isSave
            : typeof response?.data?.isSave === "boolean"
              ? response.data.isSave
              : nextLiked;

        setIsFavorited(determinedSaved);
        if (onFavorite) onFavorite(item, determinedSaved);
      } catch (err) {
        console.warn("Toggle save webinar error in AgendaItemCard:", err);
        setIsFavorited(prevLiked);
        showToast(
          lang?.something_went_wrong || "Something went wrong. Please try again.",
          "error"
        );
      }
      return;
    }

    if (isEvent) {
      try {
        const response = await toggleFavoriteEvent(cleanId);
        const resMsg = String(
          response?.message ||
          response?.data?.message ||
          response?.action ||
          response?.data?.action ||
          ""
        ).toLowerCase();

        let determinedLiked = nextLiked;
        if (
          resMsg.includes("remove") ||
          resMsg.includes("eliminad") ||
          resMsg.includes("borrad") ||
          resMsg.includes("delete") ||
          resMsg.includes("unsave")
        ) {
          determinedLiked = false;
        } else if (
          resMsg.includes("add") ||
          resMsg.includes("añadid") ||
          resMsg.includes("guardad") ||
          resMsg.includes("save")
        ) {
          determinedLiked = true;
        } else if (typeof response?.data?.is_favorite === "boolean") {
          determinedLiked = response.data.is_favorite;
        } else if (typeof response?.is_favorite === "boolean") {
          determinedLiked = response.is_favorite;
        } else if (typeof response?.data?.is_saved === "boolean") {
          determinedLiked = response.data.is_saved;
        }

        setIsFavorited(determinedLiked);
        if (onFavorite) onFavorite(item, determinedLiked);
      } catch (err) {
        console.warn("Toggle favorite error in AgendaItemCard:", err);
        setIsFavorited(prevLiked);
        showToast(
          lang?.something_went_wrong || "Something went wrong. Please try again.",
          "error"
        );
      }
    }
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    const detailsUrl = getAgendaItemDetailsUrl(item);

    if (onShare) {
      onShare(item, detailsUrl);
    }

    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(detailsUrl)
        .then(() => {
          showToast(lang?.link_copied || "Link copied to clipboard!", "success");
        })
        .catch(() => {
          showToast(lang?.link_copied || "Link copied to clipboard!", "success");
        });
    } else if (navigator.share) {
      navigator
        .share({
          title: item.title || "Agenda Event",
          text: item.description || "",
          url: detailsUrl,
        })
        .then(() => {
          showToast(lang?.shared_successfully || "Shared successfully!", "success");
        })
        .catch(() => { });
    }
  };

  let currentUserId = null;
  try {
    const signupData = JSON.parse(localStorage.getItem("SignupData") || "null");
    currentUserId = signupData?.data?.id || null;
  } catch (e) {
    currentUserId = null;
  }

  // Check creator state: If creator, do NOT show fake joined/reserved pill and host is not an attendee
  const isCreator = Boolean(
    item.isCreator ||
    item.is_creator ||
    item.is_owner ||
    (currentUserId && String(item.user?.id || item.user_id || item.creator_id) === String(currentUserId))
  );

  const rawJoinedUsers = Array.isArray(item.joinedUsers) ? item.joinedUsers : [];
  const attendeesList = isCreator && currentUserId
    ? rawJoinedUsers.filter((u) => String(u?.id) !== String(currentUserId))
    : rawJoinedUsers;

  const rawAttendeesCount =
    typeof item.attendeesCount === "number"
      ? item.attendeesCount
      : attendeesList.length;

  // If this item was created by the current user and has no attendees,
  // ensure it does not display 1 attendee due to stale/mock/fallback data.
  const attendeesCount =
    isCreator && attendeesList.length === 0 && rawAttendeesCount <= 1
      ? 0
      : rawAttendeesCount;

  const hostName =
    item.host ||
    item.user?.name ||
    [item.user?.first_name, item.user?.last_name].filter(Boolean).join(" ") ||
    item.organizer ||
    "";

  const hostRole =
    item.user?.user_title && item.user.user_title.trim().length > 0
      ? item.user.user_title
      : item.hostRole ||
      (item.type === "webinar"
        ? (lang?.speaker || "Speaker")
        : item.type === "clinical_session"
          ? (lang?.presenter || "Presenter")
          : item.type === "mentorship"
            ? (lang?.mentor || "Mentor")
            : (lang?.doctor || "Doctor"));

  const rawDescription = String(item.description || "").trim();
  const hasValidDescription =
    rawDescription.length > 0 &&
    rawDescription.toLowerCase() !== "none" &&
    rawDescription.toLowerCase() !== "null" &&
    rawDescription.toLowerCase() !== "qwe";

  const placeholderDesc =
    item.type === "webinar"
      ? (lang?.webinar_placeholder_desc || "Live interactive medical webinar with Q&A session.")
      : item.type === "clinical_session"
        ? (lang?.session_placeholder_desc || "Clinical case review and surgical procedures discussion.")
        : item.type === "mentorship"
          ? (lang?.mentorship_placeholder_desc || "Personalized 1:1 mentorship and career guidance session.")
          : (lang?.event_placeholder_desc || "Professional healthcare conference and knowledge exchange.");

  const duration = getEventDuration(item, lang);
  const formattedDate = getFormattedDateRange(item.date, item.endDate);

  const isLive =
    String(item.status || item.raw?.status || "").toLowerCase() === "started" ||
    String(item.status || item.raw?.status || "").toLowerCase() === "live";

  const isCompleted =
    viewMode === "completed" ||
    item.completed === 1 ||
    item.completed === "1" ||
    item.collection === "completed" ||
    item.isEnded === true ||
    String(item?.status || item?.raw?.status || "").toLowerCase() === "completed" ||
    String(item?.status || item?.raw?.status || "").toLowerCase() === "ended";

  const isReserved = Boolean(item.isReserved || item.raw?.is_reserved);
  const isJoined = !isCreator && Boolean(item.isJoined || item.raw?.is_joined);

  const ctaLabel =
    item.type === "webinar"
      ? (lang?.view_webinar || "View Webinar")
      : item.type === "clinical_session"
        ? (lang?.view_session || "View Session")
        : item.type === "mentorship"
          ? (lang?.view_mentorship || "View Mentorship")
          : (lang?.view_event || "View Event");

  const visibilityLabel = item.visibility || item.raw?.visibility || "Public";
  const isPrivate = visibilityLabel.toLowerCase() === "private";
  const localizedVisibility = isPrivate ? (lang?.private || "Private") : (lang?.public || "Public");

  return (
    <div
      className="agenda-grid-card"
      onClick={() => onSelect && onSelect(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect && onSelect(item);
        }
      }}
    >
      {/* ── Cover Media (Top) ── */}
      <div
        className="agenda-grid-card-cover d-flex align-items-center justify-content-center"
        style={{
          position: "relative",
          background: "#F4F7FB",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: imgLoaded ? 0 : 1,
            transition: "opacity 0.35s ease-out",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <Spinner style={{ color: "#1dc1dd", width: "10px", height: "10px", borderWidth: "1.5px" }} />
        </div>
        <img
          src={item.image || defaultAgenda1}
          alt={item.title || "Agenda event"}
          className="agenda-grid-card-img"
          onLoad={() => setImgLoaded(true)}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultAgenda1;
            setImgLoaded(true);
          }}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: imgLoaded ? 1 : 0,
            transition: "opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1), transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)",
            willChange: "opacity, transform",
          }}
        />

        {/* Top Badges: Type & Mode Pills on Left, Status/Countdown Pill on Right */}
        <div className="agenda-grid-badges-top">
          <div className="agenda-grid-badges-left">
            {/* Exact Type Pill matching Sidebar */}
            <span className={`agenda-pill-type pill-${item.type || "event"}`}>
              {getCategoryIcon(item.type, 10)}
              <span>{getCategoryLabel(item.type, false, false, lang)}</span>
            </span>

            {/* Exact Availability Pill matching Sidebar */}
            {item.isHybrid ? (
              <span className="agenda-location-badge is-hybrid" title={lang?.hybrid || "Hybrid"}>
                <LuRadio size={10} />
                <span>{lang?.hybrid || "Hybrid"}</span>
              </span>
            ) : item.isOnline ? (
              <span className="agenda-location-badge is-online" title={lang?.online || "Online"}>
                <LuVideo size={10} />
                <span>{lang?.online || "Online"}</span>
              </span>
            ) : (
              <span
                className="agenda-location-badge is-location"
                title={item.location && item.location !== "Online" ? item.location : (lang?.offline || "Offline")}
              >
                <LuMapPin size={10} />
                <span>
                  {item.location && item.location !== "Online"
                    ? item.location
                    : (lang?.offline || "Offline")}
                </span>
              </span>
            )}
          </div>

          <div className="agenda-grid-badges-right">
            {isLive ? (
              <span className="agenda-pill-countdown is-live">
                <LuRadio size={10} />
                <span>{lang?.live || "Live"}</span>
              </span>
            ) : isCompleted ? (
              <span className="agenda-pill-countdown is-completed">
                <LuCheck size={10} />
                <span>{lang?.completed || "Completed"}</span>
              </span>
            ) : item.countdown ? (
              <span
                className="agenda-pill-countdown"
                title={getCardCountdownText(item.countdown, lang)}
              >
                <LuHourglass size={10} />
                <span>{getCardCountdownText(item.countdown, lang)}</span>
              </span>
            ) : (
              <span className="agenda-pill-countdown">
                <LuHourglass size={10} />
                <span>{lang?.upcoming || "Upcoming"}</span>
              </span>
            )}
          </div>
        </div>

        {/* Bottom Corner Hover Action Buttons (Favorite & Share) - Hidden when Completed */}
        {!isCompleted && (
          <div className="agenda-grid-cover-actions">
            {(isEvent || isWebinar) && (
              <button
                type="button"
                className={`agenda-grid-cover-action-btn ${isFavorited ? "is-favorited" : ""}`}
                onClick={handleFavoriteClick}
                title={isFavorited ? (lang?.saved || "Saved") : (lang?.save || "Save")}
                aria-label="Favorite"
              >
                <HeartVectorIcon isLiked={isFavorited} size={18} />
              </button>
            )}
            <button
              type="button"
              className="agenda-grid-cover-action-btn is-share"
              onClick={handleShareClick}
              title={lang?.share || "Share (Copy link)"}
              aria-label="Share"
            >
              <LuShare2 size={16} strokeWidth={2.2} className="agenda-action-svg" />
            </button>
          </div>
        )}
      </div>

      {/* ── Card Body (Content Area) ── */}
      <div className="agenda-grid-card-body">
        {/* Title (Max 2 lines) */}
        <h4 className="agenda-grid-card-title" title={item.title}>
          <span className="agenda-grid-card-title-text">{item.title}</span>
        </h4>

        {/* Description Snippet (Clean, Max 2 lines) */}
        {hasValidDescription ? (
          <p className="agenda-grid-card-desc" title={rawDescription}>
            {rawDescription}
          </p>
        ) : (
          <p className="agenda-grid-card-desc is-placeholder">
            {placeholderDesc}
          </p>
        )}
        {/* Rich Metadata Chips Row (Date, Time, Duration, Access) */}
        <div className="agenda-grid-chips-row">
          <span className="agenda-grid-chip chip-date">
            <LuCalendar size={10} className="agenda-grid-chip-icon" />
            <span>{formattedDate}</span>
          </span>

          {item.time && (
            <span className="agenda-grid-chip chip-time">
              <LuClock size={10} className="agenda-grid-chip-icon" />
              <span>{item.time}</span>
            </span>
          )}

          {duration && (
            <span className="agenda-grid-chip chip-duration">
              <LuTimer size={10} className="agenda-grid-chip-icon" />
              <span>{duration}</span>
            </span>
          )}
          {item.type != "event" && (
            <span
              className={`agenda-grid-chip chip-access ${isPrivate ? "is-private" : "is-public"}`}
            >
              {isPrivate ? (
                <LuLock size={10} className="agenda-grid-chip-icon" />
              ) : (
                <LuGlobe size={10} className="agenda-grid-chip-icon" />
              )}
              <span>{localizedVisibility}</span>
            </span>
          )}
        </div>
        {/* Host / Doctor Row */}
        <div className="agenda-grid-doctor-row">
          <div className="agenda-grid-avatar-box">
            <img
              src={item.hostAvatar || item.user?.image || defaultAgenda1}
              alt={hostName}
              className="agenda-grid-avatar-img-single"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = defaultAgenda1;
              }}
            />
          </div>
          <span className="agenda-grid-doctor-name" title={hostName}>
            {hostName}
          </span>
          {hostRole && (
            <span className="agenda-grid-doctor-role">
              <span>{hostRole}</span>
            </span>
          )}
        </div>



        {/* Bottom Footer: Attendees Stack + Action Button / Reserved State */}
        <div className="agenda-grid-footer-row">
          <div className="agenda-grid-attendees-wrap">
            {!isMentorship && (
              <>
                {attendeesList.length > 0 ? (
                  <div className="agenda-grid-avatars-group">
                    {attendeesList.slice(0, 3).map((attendee, idx) => (
                      <img
                        key={attendee.id || idx}
                        src={attendee.image || defaultAgenda2}
                        alt={attendee.name || `Attendee ${idx + 1}`}
                        title={attendee.name}
                        className="agenda-grid-avatar-circle"
                        style={{ zIndex: 3 - idx }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = defaultAgenda2;
                        }}
                      />
                    ))}
                  </div>
                ) : null}
                {attendeesCount > 0 ? (
                  <span className="agenda-grid-attendees-text">
                    {`${attendeesCount} ${attendeesCount > 1 ? (lang?.attendees || "Attendees") : (lang?.attendee || "Attendee")}`}
                  </span>
                ) : !isCompleted ? (
                  <span className="agenda-grid-attendees-text">
                    {lang?.open_registration || "Open Registration"}
                  </span>
                ) : null}
              </>
            )}
          </div>

          <div className="agenda-grid-action-wrap">
            {isCompleted ? (
              <div className="agenda-grid-cta-btn">
                <span>
                  {isMentorship
                    ? (lang?.view_mentorship || "View Mentorship")
                    : (lang?.view_details || "View Details")}
                </span>
                <LuArrowRight size={12} className="agenda-grid-cta-arrow" />
              </div>
            ) : isReserved ? (
              <span
                className="agenda-grid-joined-pill is-reserved"
                title={lang?.reserved || "Reserved"}
              >
                <LuCheck size={11} />
                <span>{lang?.reserved || "Reserved"}</span>
              </span>
            ) : isJoined ? (
              <span
                className="agenda-grid-joined-pill is-joined"
                title={lang?.joined || "Joined"}
              >
                <LuCheck size={11} />
                <span>{lang?.joined || "Joined"}</span>
              </span>
            ) : (
              <div className="agenda-grid-cta-btn">
                <span>{ctaLabel}</span>
                <LuArrowRight size={12} className="agenda-grid-cta-arrow" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(AgendaItemCard);
